import baseApi from 'shared/api/baseApi';

import type { MediaCollection } from '../lib/mediaEntries';
import type {
  MondayBoardColumn,
  MondaySupportConfig,
  ProjectIntegrations,
  WhatsAppCallingSettings,
} from '../types/integrations';
import type { ProjectDetail, UpdateSubProjectBody } from '../ui/project-detail.types';

export type PromptType = 'main' | 'subproject' | 'common';
export type PromptLabel = 'production' | 'latest' | 'deprecated';

export interface PromptVersion {
  id: number;
  version: number;
  label: string | null;
  promptType: PromptType;
  promptName: string | null;
  content: string;
  changeReason: string;
  agentAIModel: string;
  /** Latest version only: Demo Chat runs `agentAIModel` instead of the production model. */
  isModelTest: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  createdByName: string | null;
}

interface ListPromptVersionsArgs {
  projectId: number;
  promptType?: PromptType;
  limit?: number;
  offset?: number;
}

interface SetPromptVersionModelArgs {
  projectId: number;
  promptVersionId: number;
  model: string;
}

interface ChangePromptVersionLabelArgs {
  projectId: number;
  promptVersionId: number;
  label: PromptLabel;
  /** Only for production: true makes the project use the version's model. */
  applyModel?: boolean;
}

export interface AgentModelOption {
  id: string;
  name: string;
  description: string;
  inputPricePerMillion: number | null;
  outputPricePerMillion: number | null;
  /** The model new projects get. */
  isNewProjectDefault: boolean;
}

export interface AgentModelOptionsResponse {
  /** Model the agent of this project runs in production today. */
  currentModel: string;
  /** Model Demo Chat runs: the latest version's model test, else on a subproject the master's test, else production. */
  demoChatModel: string;
  options: AgentModelOption[];
}

export interface ListPromptVersionsResponse {
  items: PromptVersion[];
  total: number;
  limit: number;
  offset: number;
  isFallback: boolean;
}

interface UpdateLookerUrlArgs {
  projectId: number;
  lookerUrl: string;
}

interface UpdateEmailProcessingArgs {
  projectId: number;
  enableInboxFolder: boolean;
  inboxFolder?: string;
  processingSchedule?: string;
}

interface UpdateEmailProcessingResponse {
  message: string;
}

interface EmailFolder {
  id: string;
  name: string;
}

interface UpdateMondaySupportArgs {
  projectId: number;
  support: MondaySupportConfig;
}

interface GetMondayBoardColumnsArgs {
  projectId: number;
  boardId?: string;
}

interface CreateMondayBoardResult {
  success: boolean;
  boardId: string;
  boardName: string;
  boardUrl: string;
  sequenceNumber?: number;
}

interface RenameProjectImageArgs {
  collection: MediaCollection;
  projectId: number;
  oldName: string;
  newName: string;
}

interface SuggestImageNamesArgs {
  projectId: number;
  items: Array<{ clientId: string; file: File }>;
}

interface SuggestImageNamesResponse {
  suggestions: Array<{ clientId: string; suggestedName: string; error?: string }>;
}

const projectsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPubsubEvents: build.query<{ pubsubEvents: string[] }, number>({
      query: (projectId) => ({
        url: `/admin/projects/events/${projectId}`,
        method: 'GET',
      }),
    }),
    addPubsubEvent: build.mutation<{ pubsubEvents: string[] }, { projectId: number; event: 'notifications' }>({
      query: ({ projectId, event }) => ({
        url: `/admin/projects/events/${projectId}`,
        method: 'PATCH',
        body: {
          event,
          action: 'add',
        },
      }),
    }),
    getWhatsAppCallingSettings: build.query<WhatsAppCallingSettings, number>({
      query: (projectId) => ({
        url: `/backoffice/projects/${projectId}/calling-settings`,
        method: 'GET',
      }),
      providesTags: (_result, _error, projectId) => [{ type: 'WhatsAppCallingSettings', id: projectId }],
    }),
    updateWhatsAppCallingSettings: build.mutation<WhatsAppCallingSettings, { projectId: number; enabled: boolean }>({
      query: ({ projectId, enabled }) => ({
        url: `/backoffice/projects/${projectId}/calling-settings`,
        method: 'POST',
        body: { enabled },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'WhatsAppCallingSettings', id: projectId }],
    }),
    getProjectIntegrations: build.query<ProjectIntegrations, number>({
      query: (projectId) => ({
        url: `/backoffice/projects/${projectId}/integrations`,
        method: 'GET',
      }),
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectIntegrations', id: projectId }],
    }),
    createMondaySuccessiveBoard: build.mutation<CreateMondayBoardResult, number>({
      query: (projectId) => ({
        url: `/admin/projects/${projectId}/monday/create-board/successive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, projectId) => [{ type: 'ProjectIntegrations', id: projectId }],
    }),
    getMondaySupport: build.query<MondaySupportConfig, number>({
      query: (projectId) => ({
        url: `/admin/projects/${projectId}/monday/support`,
        method: 'GET',
      }),
      providesTags: (_result, _error, projectId) => [{ type: 'MondaySupport', id: projectId }],
    }),
    getMondayBoardColumns: build.query<MondayBoardColumn[], GetMondayBoardColumnsArgs>({
      query: ({ projectId, boardId }) => ({
        url: `/admin/projects/${projectId}/monday/board-columns`,
        method: 'GET',
        params: boardId ? { boardId } : undefined,
      }),
    }),
    updateLookerUrl: build.mutation<{ lookerUrl: string | null }, UpdateLookerUrlArgs>({
      query: ({ projectId, lookerUrl }) => ({
        url: `/admin/projects/${projectId}`,
        method: 'PATCH',
        body: { lookerUrl },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ProjectIntegrations', id: projectId }],
    }),
    updateEmailProcessing: build.mutation<UpdateEmailProcessingResponse, UpdateEmailProcessingArgs>({
      query: ({ projectId, ...body }) => ({
        url: `/backoffice/projects/${projectId}/email-processing`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ProjectIntegrations', id: projectId }],
    }),
    getEmailFolders: build.query<EmailFolder[], number>({
      query: (projectId) => ({
        url: `/backoffice/projects/${projectId}/email-folders`,
        method: 'GET',
      }),
    }),
    updateSubProject: build.mutation<ProjectDetail, UpdateSubProjectBody & { id: number; masterProjectId: number }>({
      query: ({ id, masterProjectId: _master, ...body }) => ({
        url: `/backoffice/projects/subprojects/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id, masterProjectId }) => [
        { type: 'MasterProject', id },
        { type: 'MasterProject', id: masterProjectId },
        'MasterProject',
      ],
    }),
    renameProjectMultimedia: build.mutation<unknown, RenameProjectImageArgs>({
      query: ({ collection, projectId, oldName, newName }) => ({
        url: `/backoffice/projects/${projectId}/multimedia/rename`,
        method: 'PATCH',
        body: { collection, oldName, newName },
      }),
      invalidatesTags: ['MasterProject'],
    }),
    listPromptVersions: build.query<ListPromptVersionsResponse, ListPromptVersionsArgs>({
      query: ({ projectId, promptType, limit, offset }) => {
        const params: Record<string, string | number> = {};
        if (promptType) params.promptType = promptType;
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;
        return {
          url: `/admin/projects/${projectId}/prompt-versions`,
          method: 'GET',
          params: Object.keys(params).length ? params : undefined,
        };
      },
      providesTags: (result, _error, { projectId, promptType }) => [
        { type: 'PromptVersions', id: `${projectId}:${promptType ?? 'auto'}` },
        ...(result?.items ?? []).map((v) => ({ type: 'PromptVersions' as const, id: v.id })),
      ],
    }),
    changePromptVersionLabel: build.mutation<PromptVersion, ChangePromptVersionLabelArgs>({
      query: ({ projectId, promptVersionId, label, applyModel }) => ({
        url: `/admin/projects/${projectId}/prompt-versions/${promptVersionId}/label`,
        method: 'PATCH',
        body: { label, applyModel },
      }),
      invalidatesTags: (_result, _error, { projectId, promptVersionId, applyModel }) => [
        { type: 'PromptVersions', id: `${projectId}:common` },
        { type: 'PromptVersions', id: `${projectId}:subproject` },
        { type: 'PromptVersions', id: promptVersionId },
        // The latest version decides the Demo Chat model, and on a master it also covers its subprojects.
        'AgentModelOptions',
        ...(applyModel ? (['MasterProject'] as const) : []),
      ],
    }),
    setPromptVersionModel: build.mutation<PromptVersion, SetPromptVersionModelArgs>({
      query: ({ projectId, promptVersionId, model }) => ({
        url: `/admin/projects/${projectId}/prompt-versions/${promptVersionId}/model`,
        method: 'PATCH',
        body: { model },
      }),
      invalidatesTags: (_result, _error, { projectId, promptVersionId }) => [
        { type: 'PromptVersions', id: `${projectId}:common` },
        { type: 'PromptVersions', id: `${projectId}:subproject` },
        { type: 'PromptVersions', id: promptVersionId },
        'AgentModelOptions',
      ],
    }),
    getAgentModelOptions: build.query<AgentModelOptionsResponse, number>({
      query: (projectId) => ({
        url: `/admin/projects/${projectId}/agent-model-options`,
        method: 'GET',
      }),
      providesTags: (_result, _error, projectId) => [{ type: 'AgentModelOptions', id: projectId }],
    }),
    suggestImageNames: build.mutation<SuggestImageNamesResponse, SuggestImageNamesArgs>({
      query: ({ projectId, items }) => {
        const fd = new FormData();
        items.forEach(({ file }) => fd.append('file', file));
        return {
          url: `/backoffice/projects/${projectId}/images/suggest-names`,
          method: 'POST',
          body: fd,
        };
      },
    }),
    updateMondaySupport: build.mutation<MondaySupportConfig, UpdateMondaySupportArgs>({
      query: ({ projectId, support }) => ({
        url: `/admin/projects/${projectId}/monday/support`,
        method: 'PATCH',
        body: support,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'MondaySupport', id: projectId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPubsubEventsQuery,
  useAddPubsubEventMutation,
  useGetProjectIntegrationsQuery,
  useGetWhatsAppCallingSettingsQuery,
  useUpdateWhatsAppCallingSettingsMutation,
  useCreateMondaySuccessiveBoardMutation,
  useUpdateLookerUrlMutation,
  useUpdateEmailProcessingMutation,
  useGetEmailFoldersQuery,
  useUpdateSubProjectMutation,
  useRenameProjectMultimediaMutation,
  useSuggestImageNamesMutation,
  useListPromptVersionsQuery,
  useChangePromptVersionLabelMutation,
  useSetPromptVersionModelMutation,
  useGetAgentModelOptionsQuery,
  useGetMondaySupportQuery,
  useUpdateMondaySupportMutation,
  useGetMondayBoardColumnsQuery,
} = projectsApi;
